<script setup lang="ts">
import { ref, watch } from "vue";

/**
 * A labelled Bulma textarea wired into vee-validate. The old kit's `text-area`
 * has no Vue 3 build; this renders the same `input-wrap` markup the phila
 * inputs use, so main.scss styles the label the same way.
 */
const props = withDefaults(
  defineProps<{
    id: string;
    label?: string;
    placeholder?: string;
    modelValue?: string;
    rules?: string;
    rows?: number;
    maxlength?: number;
  }>(),
  {
    label: "",
    placeholder: "",
    modelValue: "",
    rules: "",
    rows: 3,
    maxlength: undefined,
  },
);

const emit = defineEmits(["update:modelValue"]);
const localValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (val) => {
    if (val !== localValue.value) localValue.value = val;
  },
);
watch(localValue, (val) => emit("update:modelValue", val));
</script>

<template>
  <Field
    v-slot="{ errorMessage, handleBlur, handleChange }"
    :name="id"
    :rules="rules"
    :model-value="localValue"
    :validate-on-model-update="false"
  >
    <div
      class="input-wrap input-textarea"
      :class="{ 'has-error': errorMessage }"
    >
      <label
        :for="id"
        class="label"
      >{{ label }}</label>
      <div class="control">
        <textarea
          :id="id"
          v-model="localValue"
          class="textarea"
          :placeholder="placeholder"
          :rows="rows"
          :maxlength="maxlength"
          :aria-invalid="errorMessage ? 'true' : undefined"
          :aria-describedby="errorMessage ? `${id}-error` : undefined"
          @input="handleChange"
          @blur="handleBlur"
        />
      </div>
      <p
        v-if="errorMessage"
        :id="`${id}-error`"
        class="input-error-msg help is-danger"
      >
        {{ errorMessage }}
      </p>
    </div>
  </Field>
</template>
